import React, { useCallback, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import useModals from 'hooks/useModals';
import Collection from 'components/Collection';
import { showToast } from 'actions/toast';
import { useDispatch } from 'react-redux';
import useOnNavigationBack from 'hooks/useOnNavigationBack';
import { backToPageThatCausedAuth } from 'actions/app';

const LAST_FOCUS_KEY = 'packages';

const AllPackagesPage = ({ lastFocusKeeper, packageData, isFiltering, searchParams }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { showPackageModal } = useModals();
  const title = isFiltering ? i18n.t('packages.filterTitle') : i18n.t('packages.title');

  const handleLastFocus = useCallback(() => {
    const lastFocus = lastFocusKeeper.select(LAST_FOCUS_KEY);
    if (lastFocus.hasSaved()) lastFocus.apply(lastFocus.reset);
  }, [lastFocusKeeper]);

  const showToastText = useCallback(() => {
    if (!isFiltering) return null;

    dispatch(showToast({ options: { toastText: i18n.t('packages.filterToastDescription') } }));
  }, [isFiltering, dispatch]);

  const moreButtonConfig = useMemo(() => {
    if (!isFiltering) return null;
    return {
      title: i18n.t('packages.allPackets'),
      onEnter: () => history.push('/profile/packages/all'),
    };
  }, [history, isFiltering]);

  const handlePackageEnter = useCallback(
    (item) => {
      const { status, id, domId, isAdditional } = item;
      const elementToFocus = `#${domId}`;

      lastFocusKeeper.save(LAST_FOCUS_KEY, elementToFocus);

      const packageType = isAdditional ? 'sub' : 'main';

      innerAnalyticsVolia.postEvent({
        eventGroup: 'subscription management',
        eventName: `click ${packageType} package`,
        eventValue: `package code="${id}"`,
      });

      if (status) {
        return showPackageModal(item, { elementToFocus });
      }

      history.push({
        pathname: `/profile/packages/all/${encodeURIComponent(id)}`,
        // Provide current object id and successLink further
        search: history.location.search,
      });
    },
    [history, lastFocusKeeper, showPackageModal],
  );

  useOnNavigationBack(() => {
    if (searchParams['channelId'] || searchParams['vodAssetId']) {
      // Special case
      // https://hootko.atlassian.net/browse/STVWL-564
      setTimeout(() => dispatch(backToPageThatCausedAuth()), 0);
    }
  });

  useEffect(() => {
    handleLastFocus();
    // If it's not back from another page
    if (history.action !== 'POP') {
      showToastText();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Collection
      collectionTitle={title}
      data={packageData}
      collectionType="user-packages"
      onItemEnter={handlePackageEnter}
      moreButton={moreButtonConfig}
    />
  );
};

export default AllPackagesPage;
